export type AuthMode = 'dev' | 'external'

export type RuntimeConfig = {
  apiBaseUrl: string
  requestTimeoutMs: number
  generationPollIntervalMs: number
  authMode: AuthMode
  devAuthEmail: string
  devAuthName: string
  consentPolicyVersion: string
  experimentalPreviewEnabled: boolean
  longReferenceUploadEnabled: boolean
}

const buildConfig: RuntimeConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL?.trim() ?? '',
  requestTimeoutMs: Number(import.meta.env.VITE_API_TIMEOUT_MS) || 10_000,
  generationPollIntervalMs: Number(import.meta.env.VITE_GENERATION_POLL_MS) || 2_000,
  authMode: import.meta.env.VITE_AUTH_MODE === 'external' ? 'external' : 'dev',
  devAuthEmail: import.meta.env.VITE_DEV_AUTH_EMAIL?.trim() || 'dev@example.com',
  devAuthName: import.meta.env.VITE_DEV_AUTH_NAME?.trim() || 'Lulla Developer',
  consentPolicyVersion: import.meta.env.VITE_CONSENT_POLICY_VERSION?.trim() || '2026-09-22',
  experimentalPreviewEnabled: false,
  longReferenceUploadEnabled: false,
}

let configPromise: Promise<RuntimeConfig> | null = null

const normalize = (value: Partial<RuntimeConfig>): RuntimeConfig => ({
  ...buildConfig,
  ...value,
  apiBaseUrl: (value.apiBaseUrl ?? buildConfig.apiBaseUrl).trim().replace(/\/$/, ''),
  requestTimeoutMs: Math.max(1_000, Number(value.requestTimeoutMs) || buildConfig.requestTimeoutMs),
  generationPollIntervalMs: Math.max(500, Number(value.generationPollIntervalMs) || buildConfig.generationPollIntervalMs),
  authMode: value.authMode === 'external' ? 'external' : 'dev',
  experimentalPreviewEnabled: value.experimentalPreviewEnabled === true,
  longReferenceUploadEnabled: value.longReferenceUploadEnabled === true,
})

/**
 * Loads a deployment-time configuration file first, then falls back to Vite build
 * variables. Editing /runtime-config.json is enough to point an already-built app
 * at another Lulla server; a frontend rebuild is not required.
 */
export function loadRuntimeConfig(force = false): Promise<RuntimeConfig> {
  if (!configPromise || force) {
    configPromise = fetch('/runtime-config.json', { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(async response => {
        if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) return buildConfig
        return normalize(await response.json() as Partial<RuntimeConfig>)
      })
      .catch(() => buildConfig)
  }
  return configPromise
}

export const runtimeConfigForTests = { normalize }
